// Сборка записи истории для live-режима.
//
// У потоковой записи нет taskId (задача на бэкенде не создаётся — сессия живёт в
// памяти bit-streaming и стримит фразы в браузер), поэтому автосохранение в историю
// идёт не по taskId, как у файловых работ, а по sessionId, который компонент выдаёт
// себе при старте записи. Ключ нужен для идемпотентности: historyLinks[sessionId].
import { reflowFinals, paragraphsToText } from '../components/streaming/reflow';
import { streamingStartedAt } from './meetingDate';

export const MIC_SOURCE_NAME = 'Запись с микрофона';

// «07.08.26 17-13» — как у заголовков файловых записей: дата и время без секунд.
const stamp = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${p(d.getFullYear() % 100)} `
    + `${p(d.getHours())}-${p(d.getMinutes())}`;
};

/**
 * Запись истории по состоянию live-режима или null, если сохранять нечего.
 * Сохраняем только остановленную непустую запись: на 'recording'/'paused' текст ещё
 * дописывается, и запись в историю ушла бы обрезанной.
 */
export function buildStreamingHistoryItem(state, { now = new Date() } = {}) {
  if (!state || state.phase !== 'stopped') return null;
  const finals = state.finals || [];
  if (!finals.length) return null;

  // Тот же reflow, что и на экране: история получает ровно тот текст, который видит
  // пользователь. Тайм-коды — экранный тумблер, в историю идёт чистый текст.
  const transcript = paragraphsToText(reflowFinals(finals));
  if (!transcript.trim()) return null;

  const end = finals[finals.length - 1]?.end;
  return {
    type: 'transcription',
    title: `${MIC_SOURCE_NAME} ${stamp(now)}`,
    transcript,
    // meeting_at — начало записи: по нему протокол считает сроки («до пятницы»).
    meta: Number.isFinite(end)
      ? { duration: Math.round(end), meeting_at: state.startedAt || streamingStartedAt(finals, now) } : null,
  };
}

const LIVE_PHASES = ['recording', 'paused', 'finishing'];

/**
 * Состояние live-записи из sessionStorage вкладки. Поток микрофона перезагрузку
 * не переживает, поэтому живая фаза поднимается остановленной: иначе запись с
 * фазой 'recording' не сохранялась в Историю никогда, хотя текст лежал рядом
 * (пропажа записи 19.10.2026).
 */
export function restoreStreamingState(raw) {
  if (!raw || typeof raw !== 'object') return {};
  if (!LIVE_PHASES.includes(raw.phase)) return raw;
  return { ...raw, phase: 'stopped', partial: '' };
}

/** Вопрос перед уходом с записи. Историю обещаем, только если она включена. */
export const leaveLiveQuestion = (historyEnabled) => (historyEnabled
  ? 'Идёт запись с микрофона. Уйти с экрана? Запись остановится, записанное сохраним в Историю.'
  : 'Идёт запись с микрофона. Уйти с экрана? Запись остановится, а История выключена — текст не сохранится.');

/**
 * Можно ли уйти с экрана записи. Уход размонтирует поток — запись встречи
 * обрывается; 19.10.2026 так оборвалась часовая встреча на седьмой минуте.
 */
export function leaveLiveAllowed(state, { historyEnabled = true, confirm = window.confirm } = {}) {
  if (!LIVE_PHASES.includes(state?.phase)) return true;
  return !!confirm(leaveLiveQuestion(historyEnabled));
}

/** Смена раздела приложения: запись идёт только в «Распознавании». */
export function tabChangeAllowed(from, to, state, opts) {
  if (from !== 'transcription' || to === from) return true;
  return leaveLiveAllowed(state, opts);
}
